import React, { useState, useRef } from 'react';
import { ACCEPT_INPUT, ACCEPT_LABEL, isAcceptedFile } from '../fileTypes';

const styles = {
  zone: {
    border: '2px dashed var(--border-active)',
    borderRadius: 'var(--radius-lg)',
    padding: '44px 32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'var(--blue-surface)',
  },
  zoneHover: {
    borderColor: 'var(--brand)',
    background: 'var(--brand-dim)',
  },
  zoneActive: {
    borderColor: 'var(--brand)',
    background: 'var(--brand-mid)',
    transform: 'scale(0.99)',
  },
  icon: {
    width: 48,
    height: 48,
    margin: '0 auto 16px',
    background: 'var(--bg-hover)',
    borderRadius: 'var(--radius-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 6,
  },
  sub: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
  },
  fileChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    background: 'var(--brand-dim)',
    border: '1px solid var(--brand-border)',
    borderRadius: 'var(--radius-md)',
    marginTop: 16,
  },
  fileName: {
    flex: 1,
    fontSize: 13,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-heading)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    fontSize: 11,
    color: 'var(--text-tertiary)',
    whiteSpace: 'nowrap',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-tertiary)',
    fontSize: 18,
    lineHeight: 1,
    padding: '0 2px',
    cursor: 'pointer',
    transition: 'color 0.15s',
  },
  processBtn: {
    marginTop: 20,
    width: '100%',
    padding: '12px 24px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: '0.02em',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  bulkList: {
    marginTop: 14,
    maxHeight: 220,
    overflowY: 'auto',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-card)',
  },
  bulkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderBottom: '1px solid var(--border)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  bulkClear: {
    marginTop: 10,
    background: 'none',
    border: 'none',
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
};

export default function UploadZone({ bulkAllFormats, onProcessSingle, onBulkPack }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []).filter(isAcceptedFile);
    if (!arr.length) return;
    setFiles((prev) => {
      const next = [...prev];
      const seen = new Set(next.map((f) => `${f.name}-${f.size}`));
      arr.forEach((f) => {
        const k = `${f.name}-${f.size}`;
        if (!seen.has(k)) {
          seen.add(k);
          next.push(f);
        }
      });
      return next;
    });
  };

  const handleFile = (f) => {
    if (!isAcceptedFile(f)) return;
    if (bulkAllFormats) addFiles([f]);
    else setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (bulkAllFormats) addFiles(e.dataTransfer.files);
    else handleFile(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e) => {
    if (bulkAllFormats) addFiles(e.target.files);
    else handleFile(e.target.files[0]);
    e.target.value = '';
  };

  const removeBulkAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleProcessSingle = async () => {
    if (!file) return;
    setLoading(true);
    try {
      await onProcessSingle(file);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPack = async () => {
    if (!files.length) return;
    setLoading(true);
    try {
      await onBulkPack(files);
    } finally {
      setLoading(false);
    }
  };

  const zoneStyle = {
    ...styles.zone,
    ...(isDragOver ? styles.zoneHover : {}),
  };

  const singleEnabled = !!file && !loading;
  const bulkEnabled = files.length > 0 && !loading;

  if (bulkAllFormats) {
    return (
      <div>
        <div
          style={zoneStyle}
          onClick={() => inputRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          <div style={styles.icon}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isDragOver ? 'var(--brand)' : 'var(--text-secondary)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <div style={styles.title}>
            {isDragOver ? 'Drop files here' : 'Drop one or more settlement documents'}
          </div>
          <div style={styles.sub}>
            {ACCEPT_LABEL} &nbsp;·&nbsp; duplicates skipped &nbsp;·&nbsp; max 20 files
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_INPUT}
            multiple
            onChange={handleInputChange}
          />
        </div>

        {files.length > 0 && (
          <div style={styles.bulkList}>
            {files.map((f, idx) => (
              <div key={`${f.name}-${idx}`} style={{ ...styles.bulkRow, borderBottom: idx === files.length - 1 ? 'none' : styles.bulkRow.borderBottom }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ color: 'var(--text-tertiary)' }}>{(f.size / 1024).toFixed(0)} KB</span>
                <button
                  type="button"
                  style={styles.removeBtn}
                  onClick={() => removeBulkAt(idx)}
                  onMouseOver={(e) => { e.target.style.color = 'var(--danger)'; }}
                  onMouseOut={(e) => { e.target.style.color = 'var(--text-tertiary)'; }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <button type="button" style={styles.bulkClear} onClick={() => setFiles([])}>
            Clear all
          </button>
        )}

        <button
          type="button"
          style={{
            ...styles.processBtn,
            background: bulkEnabled ? 'var(--accent)' : 'var(--bg-hover)',
            color: bulkEnabled ? 'var(--text-on-accent)' : 'var(--text-tertiary)',
            cursor: bulkEnabled ? 'pointer' : 'not-allowed',
            border: bulkEnabled ? 'none' : '1px solid var(--border)',
          }}
          disabled={!bulkEnabled}
          onClick={handleBulkPack}
        >
          {loading ? 'Starting batch…' : `↳ Process batch (${files.length} file${files.length === 1 ? '' : 's'})`}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        style={zoneStyle}
        onClick={() => !file && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <div style={styles.icon}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isDragOver ? 'var(--brand)' : 'var(--text-secondary)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
        </div>
        <div style={styles.title}>
          {isDragOver ? 'Drop to upload' : 'Drop settlement document here'}
        </div>
        <div style={styles.sub}>
          or click to browse &nbsp;·&nbsp; {ACCEPT_LABEL}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_INPUT}
          onChange={handleInputChange}
        />
      </div>

      {file && (
        <div style={styles.fileChip}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <span style={styles.fileName}>{file.name}</span>
          <span style={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</span>
          <button
            type="button"
            style={styles.removeBtn}
            onClick={() => setFile(null)}
            onMouseOver={(e) => { e.target.style.color = 'var(--danger)'; }}
            onMouseOut={(e) => { e.target.style.color = 'var(--text-tertiary)'; }}
          >
            ×
          </button>
        </div>
      )}

      <button
        type="button"
        style={{
          ...styles.processBtn,
          background: singleEnabled ? 'var(--accent)' : 'var(--bg-hover)',
          color: singleEnabled ? 'var(--text-on-accent)' : 'var(--text-tertiary)',
          cursor: singleEnabled ? 'pointer' : 'not-allowed',
          border: singleEnabled ? 'none' : '1px solid var(--border)',
        }}
        disabled={!singleEnabled}
        onClick={handleProcessSingle}
      >
        {loading ? 'Processing...' : '↳ Process document'}
      </button>
    </div>
  );
}
